import { Injectable, signal, effect, Signal } from '@angular/core';

@Injectable()
export class GridDataManager {
  private _clonedDataset = signal<any[]>([]);
  private _originalRows = signal<Map<number, any>>(new Map());
  private _editedRows = signal<Map<number, any>>(new Map());
  private _duplicatedRows = signal<any[]>([]);

  readonly clonedDataset = this._clonedDataset.asReadonly();
  readonly originalRows = this._originalRows.asReadonly();
  readonly editedRows = this._editedRows.asReadonly();
  readonly duplicatedRows = this._duplicatedRows.asReadonly();

  initializeWithDataset(datasetSignal: Signal<any[]>): void {
    effect(() => {
      const data = datasetSignal();
      this._clonedDataset.set(structuredClone(data));
    });
  }

  saveOriginal(id: number, originalItem: any): void {
    if (!this._originalRows().has(id)) {
      const current = this._originalRows();
      current.set(id, structuredClone(originalItem));
      this._originalRows.set(new Map(current));
    }
  }

  saveEdited(id: number, editedItem: any): void {
    const current = this._editedRows();
    current.set(id, editedItem);
    this._editedRows.set(new Map(current));
  }

  updateItemInClonedDataset(id: number, propertyUpdates: any): void {
    const current = this._clonedDataset();
    const itemIndex = current.findIndex(item => item.id === id);

    if (itemIndex !== -1) {
      const newDataset = [...current];
      newDataset[itemIndex] = { ...newDataset[itemIndex], ...propertyUpdates };
      this._clonedDataset.set(newDataset);
    }
  }

  addDuplicated(rows: any[]): void {
    this._duplicatedRows.update(current => [...current, ...rows]);
  }

  updateDuplicatedRow(id: number, propertyUpdates: any): void {
    const current = this._duplicatedRows();
    const itemIndex = current.findIndex(item => item.id === id);

    if (itemIndex !== -1) {
      const newRows = [...current];
      newRows[itemIndex] = { ...newRows[itemIndex], ...propertyUpdates };
      this._duplicatedRows.set(newRows);
    }
  }

  removeDuplicatedRow(id: number): void {
    const current = this._duplicatedRows();
    const filtered = current.filter(item => item.id !== id);
    this._duplicatedRows.set(filtered);
  }

  getEditedRowsArray(): any[] {
    return [...this._editedRows().values()];
  }

  getDuplicatedRowsArray(): any[] {
    return this._duplicatedRows();
  }

  cleanAll(sourceDataset: any[]): void {
    this._duplicatedRows.set([]);
    this._editedRows.set(new Map());
    this._originalRows.set(new Map());

    const cleanData = structuredClone(sourceDataset);
    this._clonedDataset.set(cleanData);
  }

  cleanDuplicated(): void {
    this._duplicatedRows.set([]);
  }

  cleanEdited(): void {
    this._editedRows.set(new Map());
    this._originalRows.set(new Map());
  }
}
